/**
 * The routes, defined in code rather than by file convention.
 *
 * Four of them, and a code-based tree makes that visible at a glance — a file-based
 * router would spread the same four across a directory whose shape is the routing table,
 * which is worth it at forty routes and noise at four. c34 and c35 add two each; the
 * moment this file stops being readable in one screen is the moment to reconsider, and
 * that is a better trigger than a convention chosen up front.
 *
 * ## `/` is the only authenticated route, and the gate is above it rather than inside it
 *
 * Every screen behind a session sits under {@link authenticatedRoute}, which renders the
 * shell only once an organization is settled. That ordering is the point: a child that
 * fetched before the org header was set would have its request refused with
 * `ORG_NOT_SPECIFIED`, and the fix would be a retry in every screen instead of a gate in
 * one place.
 */
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useState } from "react";

import { Surface } from "@packagepage/ui";

import { authClient } from "./api/auth.js";
import { setActiveOrg } from "./api/client.js";
import { ArtworkGenerator } from "./routes/ArtworkGenerator.js";
import { AuditLog } from "./routes/AuditLog.js";
import { BillingSettings } from "./routes/BillingSettings.js";
import { CreateProduct } from "./routes/CreateProduct.js";
import { Dashboard } from "./routes/Dashboard.js";
import { Domains } from "./routes/Domains.js";
import { OrganisationSettings } from "./routes/OrganisationSettings.js";
import { ProductDetail } from "./routes/ProductDetail.js";
import { ProductList } from "./routes/ProductList.js";
import { validateProductSearch } from "./routes/product-search.js";
import { RecallDetail } from "./routes/RecallDetail.js";
import { RecallNew } from "./routes/RecallNew.js";
import { SettingsLayout } from "./routes/SettingsLayout.js";
import { TeamSettings } from "./routes/TeamSettings.js";
import { ImportHistory } from "./routes/ImportHistory.js";
import { ImportNew } from "./routes/ImportNew.js";
import { ImportScreen } from "./routes/ImportScreen.js";
import { InviteAccept } from "./routes/InviteAccept.js";
import { ForgotPassword, ResetPassword } from "./routes/PasswordReset.js";
import { SignIn } from "./routes/SignIn.js";
import { AppShell, DashboardActions } from "./shell/AppShell.js";
import { OrgGate } from "./shell/OrgGate.js";

const rootRoute = createRootRoute({ component: () => <Outlet /> });

/**
 * Today, spelled the way the mock spells it — *"Tuesday 3 February"*.
 *
 * `Intl` rather than a hand-rolled formatter, and `en-GB` rather than the browser's
 * locale: `acceptance-tests.md` says the app UI is English while product content is
 * multi-language, so a date that follows the reader's locale would be the one English
 * string on the screen that moves.
 */
function today(): string {
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date());
}

/** The honest stub behind a header action whose screen is not built yet. */
function PendingNotice({ what, onDismiss }: { what: string; onDismiss: () => void }) {
  // An entry left here after its screen ships claims a feature is missing when it is not.
  // Both removed entries named a phase that closed without them — Drift Log §U48.
  const owner: Record<string, string> = {
    "Preview a scan": "the scan-preview simulator",
  };
  return (
    <Surface tone="neutral" emphasis="filled">
      <p>
        <strong>{what}</strong> is not built yet — it belongs to{" "}
        {owner[what] ?? "a later commit"}. Nothing was changed. The API behind it already
        works, so this is a screen that is missing rather than a feature that is.
      </p>
      <p>
        <button type="button" onClick={onDismiss}>
          Dismiss
        </button>
      </p>
    </Surface>
  );
}

function Authenticated(): ReactNode {
  const navigate = useNavigate();
  const [pending, setPending] = useState<string | null>(null);

  /**
   * The signed-in person — **not the organization**, which is what this passed before.
   *
   * `userName={org.name}` rendered the brand's name in the identity block, directly below
   * the same string in the header title, and the person was never named anywhere in the
   * product. It read as plausible because an organization has a name and the block has a
   * name-shaped slot; found by reading Q6's identity spec against what the block shows.
   *
   * Falls back to the organization only while the session resolves — the honest gap rather
   * than a placeholder, and it is one tick.
   */
  const session = authClient.useSession();
  const user = session.data?.user;

  return (
    <OrgGate>
      {(org) => (
        <AppShell
          orgName={org.name}
          subtitle={today()}
          userName={user?.name ?? org.name}
          userEmail={user?.email}
          role={org.role}
          headerActions={<DashboardActions onPending={setPending} />}
          onSignOut={() => {
            void authClient.signOut().then(async () => {
              // The header is module state and outlives a route change, so it is
              // cleared here rather than left pointing at an organization the next
              // session may not belong to.
              setActiveOrg(null);
              await navigate({ to: "/signin" });
            });
          }}
        >
          {pending === null ? null : (
            <PendingNotice what={pending} onDismiss={() => setPending(null)} />
          )}
          <Outlet />
        </AppShell>
      )}
    </OrgGate>
  );
}

const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authenticated",
  component: Authenticated,
});

const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/",
  component: Dashboard,
});

const productsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/products",
  validateSearch: validateProductSearch,
  component: ProductList,
});

/**
 * Declared **before** `/products/$productId`. The order is not what decides.
 *
 * `new` would otherwise match the param route and the detail screen would ask the API for a
 * product whose id is the string "new" — a 400 on a URL a user reached by pressing the
 * primary action. TanStack Router prefers static segments over dynamic ones. U78 swapped
 * `/imports/new` and `/imports/$importId` in the tree and the resolution test still read
 * `/authenticated/imports/new`, 68 of 68 green — Drift §U78. The order stays so that a
 * reader of this tree does not have to know the rule.
 */
const productNewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/products/new",
  component: CreateProduct,
});

/**
 * One product.
 *
 * A path parameter rather than a query string because this is a **resource**, not a view of
 * one: it is the URL a brand pastes into a support ticket, and `/products/019f…` says what
 * it names where `/products?id=019f…` says what it filters.
 */
const productDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/products/$productId",
  component: function ProductDetailRoute() {
    const { productId } = productDetailRoute.useParams();
    return <ProductDetail productId={productId} />;
  },
});

/**
 * The generator, under the product it is for — `/products/{id}/artwork`.
 *
 * A child of the product's URL rather than a screen of its own, because artwork **is** a
 * property of one product: §4's tab strip lists *Artwork* among the six tabs, the identity
 * of the code being printed must never be a click away from the settings that print it, and
 * the URL is the one a brand pastes into a support ticket.
 *
 * Declared after `/products/$productId` and it does not matter: the two differ in segment
 * count, so there is no ambiguity for the router to resolve. `/products/new` is the case
 * where order was load-bearing, and its own note says why.
 */
const productArtworkRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/products/$productId/artwork",
  component: function ProductArtworkRoute() {
    const { productId } = productArtworkRoute.useParams();
    return <ArtworkGenerator productId={productId} />;
  },
});

const importsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/imports",
  component: ImportHistory,
});

/** Declared **before** `/imports/$importId`, for the reason `/products/new` is — `new` is not an id. */
const importNewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/imports/new",
  component: ImportNew,
});

const importDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/imports/$importId",
  component: function ImportDetailRoute() {
    const { importId } = importDetailRoute.useParams();
    return <ImportScreen importId={importId} />;
  },
});

/**
 * The audit log — one screen, no parameters.
 *
 * Ungated in the router as well as in the API: every role may read it (U2), so there is no
 * role check here and its absence is the ruling rather than an omission. A gate in the
 * router would also be a second answer to a question `authorize.ts` already owns.
 */
const auditLogRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/audit-log",
  component: AuditLog,
});

/**
 * Custom domain setup — one screen, no parameters.
 *
 * Ungated in the router: every plan reaches the screen, and the screen renders §8's Starter
 * gate only when no domain is in service. A role check here would be a second answer to a
 * question `authorize.ts` already owns.
 */
const domainsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/domains",
  component: Domains,
});

const settingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/settings",
  component: SettingsLayout,
});

// A redirect, not a second address for the Organisation screen — Phase 5 plan Q-P7.
const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/settings/organisation", replace: true });
  },
});

const organisationSettingsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/organisation",
  component: OrganisationSettings,
});

const billingSettingsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/billing",
  component: BillingSettings,
});

const teamSettingsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/team",
  component: TeamSettings,
});

// Outside the authenticated shell: the invitee may have no session and no organisation yet.
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite/$token",
  component: function InviteRoute() {
    const { token } = inviteRoute.useParams();
    return <InviteAccept token={token} />;
  },
});

const recallNewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/recalls/new",
  validateSearch: (search: Record<string, unknown>): { product?: string } =>
    typeof search["product"] === "string" ? { product: search["product"] } : {},
  component: function RecallNewRoute() {
    const { product } = recallNewRoute.useSearch();
    return <RecallNew productId={product} />;
  },
});

const recallDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/recalls/$recallId",
  component: function RecallDetailRoute() {
    const { recallId } = recallDetailRoute.useParams();
    return <RecallDetail recallId={recallId} />;
  },
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signin",
  component: () => <SignIn mode="signin" />,
});

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: () => <SignIn mode="signup" />,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPassword,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPassword,
});

export const routeTree = rootRoute.addChildren([
  authenticatedRoute.addChildren([
    dashboardRoute,
    productsRoute,
    productNewRoute,
    productDetailRoute,
    productArtworkRoute,
    importsRoute,
    importNewRoute,
    importDetailRoute,
    auditLogRoute,
    domainsRoute,
    settingsRoute.addChildren([
      settingsIndexRoute,
      organisationSettingsRoute,
      teamSettingsRoute,
      billingSettingsRoute,
    ]),
    recallNewRoute,
    recallDetailRoute,
  ]),
  signInRoute,
  signUpRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  inviteRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
